import React from 'react';
import { useNavigate } from 'react-router-dom';
import './Freelancer.css';

// Reusable Advanced Component for IT, Loans, and Insurance
const ServiceBlock = ({ tag, title, description, features, imgUrl, reversed }) => {
  return (
    <div className={`wf-service-block ${reversed ? 'wf-reversed' : ''}`}>
      <div className="wf-service-text">
        <div className="wf-tag fade-in">{tag}</div>
        <h2 className="slide-up">{title}</h2>
        <p className="slide-up-delay">{description}</p>
        <ul className="wf-service-list slide-up-delay-2">
          {features.map((feat, index) => (
            <li key={index}><span className="check">✓</span> {feat}</li>
          ))}
        </ul>
        <button className="wf-btn-solid hover-grow">Explore Services</button>
      </div>
      <div className="wf-service-image-wrapper">
        <img src={imgUrl} alt={title} className="wf-service-image hover-zoom" />
      </div>
    </div>
  );
};

const Freelancer = () => {
  // Call the hook HERE, right at the top of your main component
  const navigate = useNavigate();

  return (
    <div className="wf-app-container">
      {/* 2. HERO SECTION */}
      <div className="wf-hero-content">
        <div className="wf-badge badge-3d">3D Artist</div>
        <h1 className="wf-hero-title">FREELANCE FREEDOM<br />AT YOUR FINGERTIPS</h1>
        <div className="wf-badge badge-ui">UI Designer</div>
        <p className="wf-hero-subtitle">Find skilled freelancers for your projects, from writing to web development, on our platform - where talent meets opportunity!</p>
      </div>

      <div className="wf-hero-gallery">
        <div className="wf-hero-gallery">
          <div className="wf-face-card f1">
            <img src="./images/freelancer/2.png" alt="Indian corporate executive man" />
          </div>
          <div className="wf-face-card f2">
            <img src="./images/freelancer/3.jpg" alt="Indian professional woman in office" />
          </div>
          <div className="wf-face-card f3">
            <img src="./images/freelancer/1.png" alt="Indian male freelancer working" />
          </div>
          <div className="wf-face-card f4">
            <img src="./images/freelancer/4.png" alt="Indian business woman looking confident" />
          </div>
          <div className="wf-face-card f5">
            <img src="./images/freelancer/5.png" alt="Indian IT professional man smiling" />
          </div>
        </div>
      </div>

      <div className="wf-features-row">
        <div className="wf-feature">
          <span className="icon">🔍</span>
          <h4>Diverse Talent Pool</h4>
          <p>Access a global network of skilled professionals spanning various industries.</p>
        </div>
        <div className="wf-feature">
          <span className="icon">💰</span>
          <h4>Cost-Effectiveness</h4>
          <p>Save on overhead costs by hiring freelancers on a project basis.</p>
        </div>
        <div className="wf-feature">
          <span className="icon">⭐</span>
          <h4>Specialized Expertise</h4>
          <p>Tap into specialized skills and niche expertise available on demand.</p>
        </div>
        <div className="wf-feature">
          <span className="icon">🌐</span>
          <h4>Access to Global Markets</h4>
          <p>Expand your reach entering new markets collaborating worldwide.</p>
        </div>
      </div>
      <div className="wf-curve-bottom"></div>

      {/* 4.5 NEW ADVANCED SECTIONS (IT, Loans, Insurance) */}
      <section className="wf-advanced-services">
        <div className="services-header text-center">
          <h2 className="slide-up">EMPOWERING YOUR INDEPENDENCE</h2>
          <p className="slide-up-delay">Everything you need to run your freelance business seamlessly.</p>
        </div>

        {/* Loans Section (Reversed Layout) */}
        <ServiceBlock
          tag="🏦 Financial Freedom"
          title="Loans"
          description="Traditional banks don't understand freelance income. We do. Access instant capital to upgrade gear or manage cash flow."
          features={[
            "No traditional pay slips required",
            "Instant approval based on platform earnings",
            "Low-interest rates & flexible terms"
          ]}
          imgUrl="./images/freelancer/cal.jpg"
          reversed={true}
        />

        {/* Insurance Section */}
        <ServiceBlock
          tag="🛡️ Total Protection"
          title="Insurance"
          description="Working for yourself shouldn't mean leaving yourself unprotected. Secure your health and your livelihood."
          features={[
            "Premium Health & Dental coverage",
            "Equipment protection (damage/theft)",
            "Professional Liability (E&O) insurance"
          ]}
          imgUrl="./images/freelancer/in.jpg"
          reversed={false}
        />

        {/* IT Section */}
        <ServiceBlock
          tag="💻 Tech & Infrastructure"
          title="IT Solutions"
          description="Focus on your craft while we handle the tech. Get access to enterprise-grade tools tailored for freelancers."
          features={[
            "Premium software suites & licenses",
            "Secure, encrypted cloud storage",
            "24/7 dedicated remote IT support"
          ]}
          imgUrl="./images/freelancer/it.jpg"
          reversed={true}
        />
      </section>

      {/* 3. CONNECT SECTION */}
      <section className="wf-connect">
        <h2 className="wf-connect-title">CONNECT. CREATE.<br />COLLABORATE</h2>
        <div className="wf-connect-grid">
          <p>Our platform offers a seamless experience, empowering freelancers to showcase their skills and thrive in workforce landscape.</p>
          <p>Whether you're a seasoned freelancer looking to expand your client base or a business in need of specialized talent.</p>
          <button className="wf-btn-outline">"Join. Thrive. Succeed."</button>
        </div>
      </section>

      {/* 5. BENTO GRID */}
      <section>
        <div className='wf-bento'>
          <div className="wf-bento-card bento-1">
            <img src="./images/freelancer/banner1.webp" alt="Indian tech team collaborating in modern office" />
            <div className="bento-overlay">
              <h3>DISCOVER TALENT THAT FITS YOU</h3>
              <p>Find top-tier professionals to help execute your vision.</p>
            </div>
          </div>
          <div className="wf-bento-card bento-2">
            <img src="./images/freelancer/banner2.jpg" alt="Focused Indian professional woman working at her desk" />
            <div className="bento-overlay">
              <h3>SEARCH FOR GREAT JOBS</h3>
              <p>Grow your freelance career with top clients.</p>
            </div>
          </div>
        </div>

        <div className='wf-bento'>
          <div className="partner-card">
            <div className="partner-content">
              <div className="partner-logo"><span className="logo-icon">✨</span> KFS</div>
              <h2 className="partner-title">THIS IS HOW <br />GOOD <span className="arrow-scribble">⤵</span> <br />COMPANIES FIND <br />GOOD PARTNERS.</h2>
              <a href="#" className="partner-link">Learn More &rarr;</a>
              <div className="partner-info-box">
                <div className="info-icon">💎</div>
                <p>Now you can access the top 1% of talent on KFS, along with a complete suite of hybrid workforce management tools.</p>
              </div>
            </div>
          </div>
          <div className="partner-image">
            <img src="./images/freelancer/banner3.jpg" alt="Indian corporate team having a productive meeting" />
          </div>
        </div>
      </section>

      {/* 6. TESTIMONIALS */}
      <section className="wf-testimonials">
        <div className="wf-curve-top"></div>
        <h2 className="wf-test-title">REAL STORIES,<br />REAL RESULTS</h2>
        <div className="wf-test-cards">
          <div className='arrow arrow-right'></div>
          <div className='arrow arrow-left'></div>

          <div className="wf-test-card tc-left">
            <div className="user"><strong>Rahul Desai</strong><br />Full Stack Developer</div>
            <div className="stars">⭐⭐⭐⭐⭐</div>
            <p>"As a freelance developer, finding a platform that genuinely values my skills is rare. KFS not only provides a seamless interface but consistently connects me with high-quality, reliable clients across India and beyond."</p>
          </div>

          <div className="wf-test-card tc-center">
            <div className="user"><strong>Priya Sharma</strong><br />UI/UX Designer</div>
            <div className="stars">⭐⭐⭐⭐⭐</div>
            <p>"Joining KFS was a major turning point in my freelance career! The intuitive dashboard made it incredibly easy to showcase my portfolio, pitch my design services to top-tier startups, and secure long-term contracts."</p>
          </div>

          <div className="wf-test-card tc-right">
            <div className="user"><strong>Rohan Gupta</strong><br />Content Strategist</div>
            <div className="stars">⭐⭐⭐⭐⭐</div>
            <p>"KFS has been instrumental in scaling my independent business. The quality of clients is unmatched, and the secure payment system gives me absolute peace of mind. I highly recommend it to any professional starting out."</p>
          </div>
        </div>

        {/* 6. CTA BOX */}
        <div className="wf-cta-box">
          <div className="cta-text">
            <h2>DON'T MISS OUT ON EXCITING PROJECTS<br />– JOIN US TODAY!</h2>
            <p>Join the community for freelance career builders off the clock, discover new skills and active endless opportunities across nations.</p>
            <div className="cta-input-group">
              {/* Your navigation button is perfectly hooked up here */}
              <button onClick={() => navigate('/contact-us')}>Join for Free</button>
            </div>
          </div>
          <div className="cta-image">
            <img src="./images/freelancer/man.png" alt="Join us" />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Freelancer;